export const CONNECTION_DETAILS_FETCH_TIMEOUT_MS = 8000;
export const CONNECTION_DETAILS_FETCH_MAX_ATTEMPTS = 2;
export const CONNECTION_DETAILS_FETCH_RETRY_DELAY_MS = 250;

export type ConnectionDetailsFetchFailureKind = "network" | "timeout";

export class ConnectionDetailsFetchError extends Error {
  readonly attempts: number;
  readonly failureKind: ConnectionDetailsFetchFailureKind;
  readonly lastError: unknown;

  constructor({
    attempts,
    failureKind,
    lastError,
    timeoutMs,
  }: {
    attempts: number;
    failureKind: ConnectionDetailsFetchFailureKind;
    lastError: unknown;
    timeoutMs: number;
  }) {
    const detail = lastError instanceof Error ? `: ${lastError.message}` : "";
    super(
      failureKind === "timeout"
        ? `Connection details request timed out after ${timeoutMs}ms (${attempts} attempts)`
        : `Connection details request failed after ${attempts} attempts${detail}`
    );
    this.name = "ConnectionDetailsFetchError";
    this.attempts = attempts;
    this.failureKind = failureKind;
    this.lastError = lastError;
  }
}

export type ConnectionDetailsFetchRetry = {
  attempt: number;
  failureKind: ConnectionDetailsFetchFailureKind;
  nextAttempt: number;
};

type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

function isRetryableNetworkError(error: unknown): boolean {
  if (error instanceof TypeError) return true;

  const message =
    error instanceof Error ? error.message.toLowerCase() : String(error).toLowerCase();
  return (
    message.includes("failed to fetch") ||
    message.includes("networkerror") ||
    message.includes("network request failed") ||
    message.includes("load failed")
  );
}

function wait(delayMs: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, delayMs));
}

export async function fetchConnectionDetailsWithRetry({
  input,
  init,
  fetchFn = fetch,
  timeoutMs = CONNECTION_DETAILS_FETCH_TIMEOUT_MS,
  maxAttempts = CONNECTION_DETAILS_FETCH_MAX_ATTEMPTS,
  retryDelayMs = CONNECTION_DETAILS_FETCH_RETRY_DELAY_MS,
  onRetry,
}: {
  input: RequestInfo | URL;
  init: RequestInit;
  fetchFn?: FetchLike;
  timeoutMs?: number;
  maxAttempts?: number;
  retryDelayMs?: number;
  onRetry?: (retry: ConnectionDetailsFetchRetry) => void;
}): Promise<{ response: Response; attemptCount: number }> {
  const boundedTimeoutMs = Math.max(1, timeoutMs);
  const boundedMaxAttempts = Math.max(1, Math.floor(maxAttempts));
  const boundedRetryDelayMs = Math.max(0, retryDelayMs);

  for (let attempt = 1; attempt <= boundedMaxAttempts; attempt += 1) {
    const controller = new AbortController();
    const externalSignal = init.signal;
    let timedOut = false;
    const abortFromExternalSignal = () => controller.abort(externalSignal?.reason);

    if (externalSignal?.aborted) {
      abortFromExternalSignal();
    } else {
      externalSignal?.addEventListener("abort", abortFromExternalSignal, { once: true });
    }

    const timeoutId = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, boundedTimeoutMs);

    try {
      const response = await fetchFn(input, {
        ...init,
        signal: controller.signal,
      });
      return { response, attemptCount: attempt };
    } catch (error) {
      if (externalSignal?.aborted) {
        throw error;
      }

      const failureKind: ConnectionDetailsFetchFailureKind = timedOut ? "timeout" : "network";
      const canRetry = attempt < boundedMaxAttempts && (timedOut || isRetryableNetworkError(error));

      if (!canRetry) {
        throw new ConnectionDetailsFetchError({
          attempts: attempt,
          failureKind,
          lastError: error,
          timeoutMs: boundedTimeoutMs,
        });
      }

      onRetry?.({
        attempt,
        failureKind,
        nextAttempt: attempt + 1,
      });
      await wait(boundedRetryDelayMs);
    } finally {
      clearTimeout(timeoutId);
      externalSignal?.removeEventListener("abort", abortFromExternalSignal);
    }
  }

  throw new Error("Connection details fetch exhausted without a response");
}
